import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { supabase } from "../../logic/client";
import { useI18n } from "../../i18n/I18nProvider";

type Props = {
  handlePageChange: (page: string) => void;
  nextPage?: string;
};

type LabScoreRow = {
  aware_points: number | null;
  prepare_points: number | null;
  lab_score: number | null;
  start_score: number | null;
  final_score: number | null;
  score_delta: number | null;
  score_band: "Exposed" | "Developing" | "Operationally Ready";
  availability_signal: "High" | "Medium" | "Low" | null;
  top_strength: string | null;
  top_gap: string | null;
  next_action: string | null;
};

type PrepareQuestion = {
  id: string;
  question_code: string | null;
  question_en: string;
  question_de: string;
};

type PrepareAnswer = {
  question_id: string;
  answer: string;
  weight: number | null;
};

type PrepareInsight = {
  code: string;
  label: string;
  weight: number;
};

type JourneyPoint = {
  code: string;
  label: string;
  sublabel: string;
  score: number;
  delta: number;
};

const PREPARE_LABELS: Record<string, { en: string; de: string }> = {
  M1: { en: "72-hour basics", de: "72-Stunden-Grundlagen" },
  M2: { en: "Emergency backpack", de: "Notfallrucksack" },
  M3: { en: "Critical documents", de: "Wichtige Dokumente" },
  M4: { en: "Evacuation route", de: "Evakuierungsroute" },
  M5: { en: "First aid actions", de: "Erste-Hilfe-Handlungen" },
  M6: { en: "Offline communication", de: "Offline-Kommunikation" },
  M7: { en: "Decision routine", de: "Entscheidungsroutine" },
  M8: { en: "Physical evacuation capacity", de: "Körperliche Evakuierungsfähigkeit" },
};

const NEXT_ACTIONS: Record<string, { en: string; de: string }> = {
  M1: {
    en: "Check water, food, light and medication at home and close one missing 72-hour item.",
    de: "Prüfe Wasser, Nahrung, Licht und Medikamente zu Hause und schließe eine konkrete 72-Stunden-Lücke.",
  },
  M2: {
    en: "Put one grab-ready emergency backpack in a fixed place.",
    de: "Lege einen griffbereiten Notfallrucksack an einen festen Ort.",
  },
  M3: {
    en: "Collect key documents in one folder and create a secure digital backup.",
    de: "Sammle wichtige Dokumente in einer Mappe und erstelle eine sichere digitale Kopie.",
  },
  M4: {
    en: "Choose a first safe destination, a backup destination and one route without public transport.",
    de: "Lege ein erstes sicheres Ziel, ein Ausweichziel und eine Route ohne öffentliche Verkehrsmittel fest.",
  },
  M5: {
    en: "Refresh one first-aid action and book a practical training if yours is outdated.",
    de: "Frische eine Erste-Hilfe-Handlung auf und buche ein Praxistraining, falls deins veraltet ist.",
  },
  M6: {
    en: "Write down key contacts offline and define one radio or official information source.",
    de: "Notiere wichtige Kontakte offline und lege eine Radio- oder offizielle Informationsquelle fest.",
  },
  M7: {
    en: "Create one simple crisis rule: pause, check facts, choose the next safe action.",
    de: "Erstelle eine einfache Krisenregel: innehalten, Fakten prüfen, nächste sichere Handlung wählen.",
  },
  M8: {
    en: "Walk a realistic evacuation route once with the bag or support need you would actually have.",
    de: "Teste einmal eine realistische Evakuierungsroute mit dem Gepäck oder Unterstützungsbedarf, den du wirklich hättest.",
  },
};

const MODULE_STEPS = [
  { code: "M1", en: "Home", de: "Zuhause" },
  { code: "M2", en: "Bag", de: "Rucksack" },
  { code: "M3", en: "Docs", de: "Dokumente" },
  { code: "M4", en: "Route", de: "Route" },
  { code: "M5", en: "Medical", de: "Medizin" },
  { code: "M6", en: "Comms", de: "Kontakt" },
  { code: "M7", en: "Mental", de: "Mental" },
  { code: "M8", en: "Physical", de: "Körper" },
];

const ACCENT = "#d6293e";
const ACCENT_SOFT = "#f05969";
const ACCENT_DARK = "#b50d20";
const TILE_BG = "rgba(255,255,255,0.06)";
const TILE_BORDER = "rgba(255,255,255,0.12)";

function toNumber(value: number | string | null | undefined) {
  const numeric = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function getLocalized(
  lang: string,
  value: { en: string; de: string } | undefined,
  fallback: string
) {
  if (!value) return fallback;
  return lang === "de" ? value.de : value.en;
}

function getModuleNumber(code: string) {
  const match = code.match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function formatSigned(value: number, formatter: (value: number) => string) {
  return `${value >= 0 ? "+" : ""}${formatter(value)}`;
}

export default function PrepareResultScreen({
  handlePageChange,
  nextPage = "JourneyPillars",
}: Props) {
  const { lang } = useI18n();
  const isDE = lang === "de";

  const [score, setScore] = React.useState<LabScoreRow | null>(null);
  const [insights, setInsights] = React.useState<PrepareInsight[]>([]);
  const [loading, setLoading] = React.useState(true);

  const formatScore = React.useCallback(
    (value: number) =>
      new Intl.NumberFormat(isDE ? "de-DE" : "en-US", {
        maximumFractionDigits: 1,
      }).format(value),
    [isDE]
  );

  React.useEffect(() => {
    let mounted = true;

    async function loadResults() {
      setLoading(true);

      const { data: employeeId } = await supabase.rpc("my_employee_id");

      if (!employeeId) {
        if (mounted) setLoading(false);
        return;
      }

      await supabase.rpc("recalculate_lab_score", {
        p_employee_id: employeeId,
      });

      const [{ data: scoreRow }, { data: questions }, { data: answers }] = await Promise.all([
        supabase
          .from("lab_scores")
          .select(
            "aware_points, prepare_points, lab_score, start_score, final_score, score_delta, score_band, availability_signal, top_strength, top_gap, next_action"
          )
          .eq("employee_id", employeeId)
          .maybeSingle(),
        supabase
          .from("vibe_questions_v2")
          .select("id, question_code, question_en, question_de")
          .eq("category", "prepare")
          .order("order_index", { ascending: true }),
        supabase
          .from("employee_vibe_answers_v2")
          .select("question_id, answer, weight")
          .eq("employee_id", employeeId),
      ]);

      const questionById = new Map(
        ((questions ?? []) as PrepareQuestion[]).map((question) => [
          question.id,
          question,
        ])
      );

      const derivedInsights = ((answers ?? []) as PrepareAnswer[])
        .map((answer) => {
          const question = questionById.get(answer.question_id);
          const code = question?.question_code ?? "";

          if (!code) return null;

          return {
            code,
            label: getLocalized(
              lang,
              PREPARE_LABELS[code],
              isDE ? question?.question_de ?? code : question?.question_en ?? code
            ),
            weight: toNumber(answer.weight),
          };
        })
        .filter(Boolean) as PrepareInsight[];

      if (mounted) {
        setScore(scoreRow as LabScoreRow | null);
        setInsights(derivedInsights);
        setLoading(false);
      }
    }

    loadResults();

    return () => {
      mounted = false;
    };
  }, [isDE, lang]);

  const startScore = toNumber(score?.start_score ?? score?.aware_points);
  const finalScore = toNumber(score?.final_score ?? score?.lab_score);
  const delta = toNumber(score?.score_delta ?? finalScore - startScore);
  const progressValue = Math.min(100, Math.max(0, finalScore));

  const sortedByStrength = [...insights].sort((a, b) => {
    const weightDiff = b.weight - a.weight;
    return weightDiff !== 0 ? weightDiff : getModuleNumber(a.code) - getModuleNumber(b.code);
  });
  const sortedByGap = [...insights].sort((a, b) => {
    const weightDiff = a.weight - b.weight;
    return weightDiff !== 0 ? weightDiff : getModuleNumber(a.code) - getModuleNumber(b.code);
  });
  const topStrength = sortedByStrength[0] ?? null;
  const topGap =
    sortedByGap.find((insight) => insight.code !== topStrength?.code) ??
    sortedByGap[0] ??
    null;
  const storedStrength = score?.top_strength?.trim() ?? "";
  const storedGap = score?.top_gap?.trim() ?? "";
  const storedLabelsAreDistinct = Boolean(
    storedStrength && storedGap && storedStrength !== storedGap
  );

  const topStrengthLabel =
    (storedLabelsAreDistinct ? storedStrength : "") ||
    topStrength?.label ||
    (isDE ? "Noch keine Stärke ermittelt" : "No strength identified yet");
  const topGapLabel =
    (storedLabelsAreDistinct ? storedGap : "") ||
    topGap?.label ||
    (isDE ? "Noch keine Lücke ermittelt" : "No remaining gap identified yet");
  const nextAction =
    score?.next_action ||
    getLocalized(
      lang,
      topGap ? NEXT_ACTIONS[topGap.code] : undefined,
      isDE
        ? "Wähle diese Woche eine kleine Vorbereitungslücke und schließe sie konkret."
        : "Choose one small readiness gap this week and close it concretely."
    );

  const weightsByCode = new Map(insights.map((insight) => [insight.code, insight.weight]));
  const totalKnownDelta = MODULE_STEPS.reduce(
    (total, step) => total + toNumber(weightsByCode.get(step.code)),
    0
  );
  const fallbackStepDelta =
    totalKnownDelta === 0 && delta !== 0 ? delta / MODULE_STEPS.length : 0;

  let runningScore = startScore;
  const journeyPoints: JourneyPoint[] = [
    {
      code: "Aware",
      label: "Aware",
      sublabel: "",
      score: startScore,
      delta: 0,
    },
    ...MODULE_STEPS.map((step, index) => {
      const previousScore = runningScore;
      const rawDelta = toNumber(weightsByCode.get(step.code)) || fallbackStepDelta;
      const isLast = index === MODULE_STEPS.length - 1;
      const pointScore = isLast ? finalScore : previousScore + rawDelta;

      runningScore = previousScore + rawDelta;

      return {
        code: step.code,
        label: step.code,
        sublabel: isDE ? step.de : step.en,
        score: pointScore,
        delta: pointScore - previousScore,
      };
    }),
  ];

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 1.5, sm: 3 },
        py: { xs: 2.5, sm: 5, md: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, rgba(16,16,18,1) 0%, rgba(48,27,27,1) 100%)",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(92vw, 980px)", lg: "min(82vw, 1120px)" },
          maxWidth: "100%",
          overflow: "hidden",
          borderRadius: { xs: 3, sm: 5 },
          p: { xs: 2, sm: 4, md: 5, lg: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        {loading ? (
          <Stack spacing={2.5} alignItems="center">
            <CircularProgress sx={{ color: ACCENT }} />
            <Typography sx={{ color: "rgba(255,255,255,0.82)" }}>
              {isDE ? "BEREIT Ergebnis wird berechnet..." : "Calculating your PREPARE result..."}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={{ xs: 2.5, sm: 3.5, md: 4 }} alignItems="center">
            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: "1.45rem", sm: "2.05rem", md: "2.25rem" },
                  fontWeight: 950,
                  lineHeight: 1.15,
                  mb: 1.5,
                }}
              >
                {isDE ? "Dein BEREIT Ergebnis ist da." : "Your PREPARE result is ready."}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.76)",
                  lineHeight: 1.65,
                  maxWidth: 720,
                  mx: "auto",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                {isDE
                  ? "Du siehst deinen aktuellen Lab Score, deine Score Journey und eine konkrete nächste Handlung."
                  : "You can see your current Lab Score, your score journey and one concrete next action."}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "0.82fr 1.18fr" },
                gap: 2,
                alignItems: "stretch",
                minWidth: 0,
              }}
            >
              <VisualTile>
                <Typography sx={tileLabelSx}>
                  {isDE ? "Dein Lab Score" : "Your Lab Score"}
                </Typography>
                <ScoreGauge value={progressValue} label={formatScore(finalScore)} />
                <Typography sx={{ color: "rgba(255,255,255,0.68)", mt: 1, fontWeight: 700 }}>
                  {isDE
                    ? `Bewusst ${formatScore(startScore)} -> Jetzt ${formatScore(finalScore)} (${formatSigned(delta, formatScore)})`
                    : `Aware ${formatScore(startScore)} -> Now ${formatScore(finalScore)} (${formatSigned(delta, formatScore)})`}
                </Typography>
              </VisualTile>

              <VisualTile>
                <Typography sx={tileLabelSx}>
                  {isDE ? "Score Journey" : "Score journey"}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.58)",
                    lineHeight: 1.5,
                    mb: 1,
                  }}
                >
                  {isDE
                    ? "Jedes Modul bewegt denselben Lab Score weiter."
                    : "Every module moves the same Lab Score forward."}
                </Typography>
                <ScoreJourneyChart points={journeyPoints} formatScore={formatScore} />
              </VisualTile>
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ width: "100%" }}
            >
              <ResultTile
                label={isDE ? "Band" : "Band"}
                value={score?.score_band ?? "Exposed"}
              />
              <ResultTile
                label={isDE ? "Verfügbarkeit" : "Availability"}
                value={score?.availability_signal ?? "Low"}
              />
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ width: "100%" }}
            >
              <ResultTile
                label={isDE ? "Top-Stärke" : "Top strength"}
                value={topStrengthLabel}
                accent="#35bf78"
              />
              <ResultTile
                label={isDE ? "Größte verbleibende Lücke" : "Top remaining gap"}
                value={topGapLabel}
                accent={ACCENT_SOFT}
              />
            </Stack>

            <Box
              sx={{
                width: "100%",
                borderRadius: 3,
                p: { xs: 2.5, sm: 3 },
                background: `linear-gradient(90deg, ${ACCENT_DARK}, ${ACCENT})`,
                textAlign: "left",
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.78)", fontWeight: 900, mb: 1 }}>
                {isDE ? "Eine Aktion diese Woche" : "One action this week"}
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  lineHeight: 1.45,
                  fontSize: { xs: "1.05rem", sm: "1.18rem" },
                }}
              >
                {nextAction}
              </Typography>
            </Box>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.42)",
                maxWidth: 760,
                lineHeight: 1.55,
                fontSize: "0.95rem",
              }}
            >
              {isDE
                ? "Der Score wird aus 100 Punkten gezeigt. Formel und Teil-Scores werden nicht angezeigt. Dein Ergebnis wird nie mit anderen Teilnehmenden verglichen."
                : "Score is shown out of 100. Formula and sub-scores are not shown. Your result is never compared to other participants."}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => handlePageChange("PrepareScenarioM1")}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  px: { xs: 2.5, sm: 4 },
                  py: 1.25,
                  width: { xs: "100%", sm: "auto" },
                  borderColor: "rgba(255,255,255,0.4)",
                  color: "#fff",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {isDE ? "BEREIT-Module erneut machen" : "Retake PREPARE modules"}
              </Button>

              <Button
                variant="contained"
                onClick={() => handlePageChange(nextPage)}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  px: { xs: 2.5, sm: 4 },
                  py: 1.25,
                  width: { xs: "100%", sm: "auto" },
                  background: `linear-gradient(90deg, ${ACCENT_DARK}, ${ACCENT})`,
                  "&:hover": {
                    background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                  },
                }}
              >
                {nextPage === "JourneyPillars"
                  ? isDE
                    ? "Zurück zur Homepage"
                    : "Back to Homepage"
                  : isDE
                    ? "Weiter zu SHARE"
                    : "Continue to SHARE"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

const tileLabelSx = {
  color: "rgba(255,255,255,0.58)",
  fontWeight: 900,
  mb: 1,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontSize: "0.82rem",
};

function VisualTile(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: TILE_BG,
        border: `1px solid ${TILE_BORDER}`,
        textAlign: "left",
      }}
    >
      {props.children}
    </Box>
  );
}

function ResultTile(props: { label: string; value: string; accent?: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 3,
        backgroundColor: TILE_BG,
        border: `1px solid ${TILE_BORDER}`,
        textAlign: "left",
        borderLeft: props.accent ? `4px solid ${props.accent}` : undefined,
      }}
    >
      <Typography sx={{ color: props.accent ?? "rgba(255,255,255,0.58)", fontWeight: 800, mb: 0.75 }}>
        {props.label}
      </Typography>
      <Typography sx={{ color: "#fff", fontWeight: 900, lineHeight: 1.35 }}>
        {props.value}
      </Typography>
    </Box>
  );
}

function ScoreGauge(props: { value: number; label: string }) {
  const radius = 78;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - props.value / 100);

  return (
    <Box
      sx={{
        width: { xs: "min(58vw, 184px)", sm: 230, md: 250, lg: 260 },
        maxWidth: "100%",
        aspectRatio: "1 / 1",
        mx: "auto",
        position: "relative",
      }}
    >
      <svg viewBox="0 0 220 220" width="100%" height="100%" aria-hidden="true">
        <circle
          cx="110"
          cy="110"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="22"
        />
        <circle
          cx="110"
          cy="110"
          r={radius}
          fill="none"
          stroke={ACCENT_SOFT}
          strokeWidth="22"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 110 110)"
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pt: 1,
        }}
      >
        <Typography
          sx={{
            color: "#fff",
            fontSize: { xs: "2.55rem", sm: "3.45rem", md: "3.8rem", lg: "4rem" },
            fontWeight: 950,
            lineHeight: 1,
          }}
        >
          {props.label}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: { xs: "0.95rem", sm: "1.15rem" }, fontWeight: 800, mt: 0.5 }}>
          /100
        </Typography>
      </Box>
    </Box>
  );
}

function ScoreJourneyChart(props: {
  points: JourneyPoint[];
  formatScore: (value: number) => string;
}) {
  const plot = { left: 40, right: 560, top: 32, bottom: 154 };
  const scores = props.points.map((point) => point.score);
  const minScore = Math.min(30, ...scores);
  const maxScore = Math.max(85, ...scores);
  const xFor = (index: number) =>
    plot.left + (index * (plot.right - plot.left)) / Math.max(1, props.points.length - 1);
  const yFor = (score: number) =>
    plot.bottom - ((score - minScore) / (maxScore - minScore)) * (plot.bottom - plot.top);
  const coords = props.points.map((point, index) => ({
    ...point,
    x: xFor(index),
    y: yFor(point.score),
  }));
  const linePath = coords
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
  const fillPath = `${linePath} L ${coords[coords.length - 1].x} ${plot.bottom} L ${coords[0].x} ${plot.bottom} Z`;
  const yTicks = [35, 50, 65, 80];

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "600 / 240",
          minHeight: { xs: 170, sm: 200 },
        }}
      >
        <svg
          viewBox="0 0 600 240"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Prepare score journey"
        >
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={plot.left}
                x2={plot.right}
                y1={yFor(tick)}
                y2={yFor(tick)}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
              />
              <text
                x={plot.left - 14}
                y={yFor(tick) + 4}
                textAnchor="end"
                fill="rgba(255,255,255,0.36)"
                fontSize="10"
                fontWeight="700"
              >
                {tick}
              </text>
            </g>
          ))}
          <path d={fillPath} fill="rgba(240,89,105,0.13)" />
          <path
            d={linePath}
            fill="none"
            stroke={ACCENT_SOFT}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {coords.map((point, index) => {
            const isFirst = index === 0;
            const isLast = index === coords.length - 1;
            const showDelta = !isFirst && !isLast && Math.abs(point.delta) > 0.01;

            return (
              <g key={point.code}>
                {showDelta && (
                  <text
                    x={point.x}
                    y={point.y - 12}
                    textAnchor="middle"
                    fill={ACCENT_SOFT}
                    fontSize="10"
                    fontWeight="900"
                  >
                    {formatSigned(point.delta, props.formatScore)}
                  </text>
                )}
                {(isFirst || isLast) && (
                  <text
                    x={point.x}
                    y={point.y - 14}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={isLast ? "13" : "11"}
                    fontWeight="900"
                  >
                    {props.formatScore(point.score)}
                  </text>
                )}
                {isLast && <circle cx={point.x} cy={point.y} r="18" fill="rgba(240,89,105,0.2)" />}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isLast ? 9 : 6}
                  fill={isLast ? ACCENT_SOFT : "rgba(49,32,32,0.95)"}
                  stroke={ACCENT_SOFT}
                  strokeWidth="4"
                />
                <text
                  x={point.x}
                  y={plot.bottom + 28}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.58)"
                  fontSize="10"
                  fontWeight="800"
                >
                  {point.label}
                </text>
                {point.sublabel && (
                  <text
                    x={point.x}
                    y={plot.bottom + 44}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.36)"
                    fontSize="8"
                    fontWeight="700"
                  >
                    {point.sublabel}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
}
